import { HttpErrorResponse } from '@angular/common/http';
import { ApiException } from '../../shared/services/service-proxy/service-proxy';
import { ApiResponse } from './auth-contracts';

/** Shown when the API is unreachable, or answers 502/503/504 without the JSON envelope (Azure cold start). */
export const SERVER_UNREACHABLE_MESSAGE = 'We could not reach the server. Try again shortly.';

export const GENERIC_ERROR_MESSAGE =
  'We could not complete your request. Try again, or contact support if this keeps happening.';

/** A failed API call: `HttpErrorResponse` from HttpClient, `ApiException` from the generated client. */
export function isApiError(error: unknown): error is HttpErrorResponse | ApiException {
  return error instanceof HttpErrorResponse || error instanceof ApiException;
}

/** The envelope's own message, or null when the API sent none, so a page can choose its own fallback text. */
export function apiEnvelopeMessage(error: unknown): string | null {
  if (!isApiError(error)) {
    return null;
  }

  // The generated client hands over the body as raw text.
  const body: unknown = error instanceof ApiException ? parseJson(error.response) : error.error;

  return isEnvelope(body) && typeof body.message === 'string' && body.message.trim() !== '' ? body.message : null;
}

/** The message a page shows for a failed API call: the envelope's message, never a status code or raw text. */
export function apiErrorMessage(error: unknown): string {
  if (!isApiError(error)) {
    return GENERIC_ERROR_MESSAGE;
  }

  const message = apiEnvelopeMessage(error);

  if (message !== null) {
    return message;
  }

  if (error.status === 0 || error.status === 502 || error.status === 503 || error.status === 504) {
    return SERVER_UNREACHABLE_MESSAGE;
  }

  return GENERIC_ERROR_MESSAGE;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // Not JSON (an HTML error page, an empty body): no envelope to read.
    return null;
  }
}

function isEnvelope(value: unknown): value is ApiResponse<unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as ApiResponse<unknown>).succeeded === 'boolean'
  );
}
